/**
 * Voice-session Zustand store — **call lifecycle only**.
 *
 * As of the voice-as-chat-channel cutover, a voice call is a projection of a
 * chat session: every spoken turn is persisted + broadcast by the backend as a
 * regular `ChatMessage` into the bound chat session, and the FE renders it from
 * `useChatStore`. So this store no longer owns transcript / active-context /
 * redaction state — those live on the chat thread + the per-turn grounding
 * reports now. It tracks only what the call itself needs: connection status,
 * the EL credentials, the bound chat session id, and the EL voice cost/duration.
 *
 * State split:
 *   - Persisted (key `lvdr_session_voice` — wiped by `hardLogout`):
 *       `lastProjectId` only. Useful so a quick reload after a crash can offer a
 *       "reopen" affordance — but never persists secrets.
 *   - In-memory:
 *       `sessionId`, `bearer`, `customLlmUrl`, `agentId`, `chatSessionId`,
 *       `projectId`, `status`, `costUsd`, `durationSec`, `error`, plus per-call
 *       load-state timers.
 *
 * Per CLAUDE.md "Frontend (Zustand + WebSocket)" rules:
 *   - WS load states have a per-request 30s timeout→error path (keyed in a
 *     Map, not a single module slot).
 *   - Fetches that can be superseded use an AbortController. `startSession`
 *     is single-shot per call; we still own a controller for the fetch so an
 *     unmount-during-start abort is clean.
 *   - Bearer + customLlmUrl + agentId are NEVER persisted — secrets stay
 *     in memory only.
 */
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { AuthError } from '@/lib/http';
import { useVoiceConsentStore } from '@/features/privacy/useVoiceConsentStore';
import { endVoiceSession, startVoiceSession } from './api';

const PERSIST_KEY = 'lvdr_session_voice';
const SESSION_START_TIMEOUT_MS = 30_000;

export type VoiceStatus = 'idle' | 'connecting' | 'open' | 'closing' | 'closed' | 'error';

interface VoicePersistedState {
  lastProjectId: string | null;
}

interface VoiceTransientState {
  sessionId: string | null;
  agentId: string | null;
  customLlmUrl: string | null;
  /**
   * Plaintext per-session bearer. **In-memory only.** Forwarded to the
   * ElevenLabs SDK via the customLlm config; not exposed elsewhere.
   */
  bearer: string | null;
  projectId: string | null;
  /**
   * The chat session this call is bound to. Every voice turn is persisted +
   * broadcast under this id, so the chat thread and the call share one
   * conversation. Set at `startSession` time from the panel's active session.
   */
  chatSessionId: string | null;
  status: VoiceStatus;
  /** Running EL voice (ASR+TTS) cost in USD, as metered by the backend. */
  costUsd: number;
  durationSec: number;
  error: string | null;
}

interface VoiceActions {
  startSession: (projectId: string, chatSessionId?: string) => Promise<void>;
  endSession: (reason?: string) => Promise<void>;
  reset: () => void;
}

export type VoiceSessionState = VoicePersistedState &
  VoiceTransientState & {
    actions: VoiceActions;
  };

const initialTransient: VoiceTransientState = {
  sessionId: null,
  agentId: null,
  customLlmUrl: null,
  bearer: null,
  projectId: null,
  chatSessionId: null,
  status: 'idle',
  costUsd: 0,
  durationSec: 0,
  error: null,
};

// Per-call timers — keyed by `projectId` so two start-sessions in flight
// (e.g. fast project-swap) each get their own watchdog without clobbering.
const _startTimers = new Map<string, ReturnType<typeof setTimeout>>();
const _startAborts = new Map<string, AbortController>();

function clearStartGuard(projectId: string): void {
  const t = _startTimers.get(projectId);
  if (t) clearTimeout(t);
  _startTimers.delete(projectId);
  const c = _startAborts.get(projectId);
  if (c) c.abort();
  _startAborts.delete(projectId);
}

export const useVoiceSession = create<VoiceSessionState>()(
  persist(
    (set, get) => ({
      // Persisted slice
      lastProjectId: null,

      // In-memory slice
      ...initialTransient,

      actions: {
        startSession: async (projectId: string, chatSessionId?: string) => {
          // Already open for this project? No-op. Guards a StrictMode
          // double-mount in dev as well.
          const current = get();
          if (
            current.projectId === projectId &&
            (current.status === 'open' || current.status === 'connecting')
          ) {
            return;
          }

          // Tear down any in-flight start for a different project.
          if (current.projectId && current.projectId !== projectId) {
            clearStartGuard(current.projectId);
          }

          set({
            ...initialTransient,
            lastProjectId: projectId,
            projectId,
            chatSessionId: chatSessionId ?? null,
            status: 'connecting',
          });

          const controller = new AbortController();
          _startAborts.set(projectId, controller);
          const timer = setTimeout(() => {
            controller.abort();
            // Only flip to error if we're still connecting for the same project.
            const st = get();
            if (st.projectId === projectId && st.status === 'connecting') {
              set({
                status: 'error',
                error: 'Voice session start timed out after 30s',
              });
            }
            _startTimers.delete(projectId);
            _startAborts.delete(projectId);
          }, SESSION_START_TIMEOUT_MS);
          _startTimers.set(projectId, timer);

          try {
            const args: import('./api').SessionStartArgs = {
              projectId,
              signal: controller.signal,
            };
            // Only thread chatSessionId when present — an empty string on the
            // wire would bind the call to no session (protobuf-empty-erases).
            if (chatSessionId) args.chatSessionId = chatSessionId;
            const res = await startVoiceSession(args);
            // Was this start superseded by a different project? Drop the result.
            if (get().projectId !== projectId) {
              return;
            }
            set({
              sessionId: res.sessionId,
              agentId: res.agentId ?? null,
              customLlmUrl: res.customLlmUrl,
              bearer: res.bearer,
              status: 'open',
              error: null,
            });
          } catch (err) {
            // AbortError: either the watchdog tripped (state already flipped
            // to error above) or the page unmounted (project changed). Either
            // way nothing more to do.
            if (err instanceof DOMException && err.name === 'AbortError') {
              return;
            }
            // R28-P5: the BE gate refused the call because consent isn't on file
            // (e.g. withdrawn on another device, or the disclosure version bumped).
            // Re-surface the disclosure prompt instead of a dead-end error, and
            // refresh the cached record so the mic reflects the true state.
            if (err instanceof AuthError && err.code === 'voice_consent_required') {
              const c = useVoiceConsentStore.getState();
              void c.actions.load();
              c.actions.openPrompt();
              if (get().projectId === projectId) {
                set({ status: 'idle', error: null });
              }
              return;
            }
            if (get().projectId === projectId) {
              set({
                status: 'error',
                error: err instanceof Error ? err.message : 'Failed to start voice session',
              });
            }
          } finally {
            clearStartGuard(projectId);
          }
        },

        endSession: async (reason?: string) => {
          const { sessionId, status } = get();
          if (!sessionId) {
            // Clean teardown anyway — clears cost/duration for the next call.
            set({ ...initialTransient, lastProjectId: get().lastProjectId });
            return;
          }
          if (status === 'closing' || status === 'closed') return;
          set({ status: 'closing' });
          try {
            // Voice turns were persisted into the bound chat session as they
            // happened (BE AppendAndBroadcast), so there is no transcript to
            // materialize on hang-up — just close the voice session.
            const args: import('./api').SessionEndArgs = { sessionId };
            if (reason !== undefined) args.reason = reason;
            const res = await endVoiceSession(args);
            set({
              status: 'closed',
              costUsd: res.totalCostUsd,
            });
          } catch (err) {
            // End-session failure is non-fatal for the FE — the backend has
            // an idle sweeper that closes orphan sessions. Just surface the
            // error so users see it, and let them navigate away.
            set({
              status: 'error',
              error: err instanceof Error ? err.message : 'Failed to end voice session',
            });
          }
        },

        reset: () => {
          const { projectId } = get();
          if (projectId) clearStartGuard(projectId);
          set({ ...initialTransient, lastProjectId: get().lastProjectId });
        },
      },
    }),
    {
      name: PERSIST_KEY,
      storage: createJSONStorage(() =>
        typeof window !== 'undefined' ? window.localStorage : (undefined as unknown as Storage),
      ),
      // Persist only the non-secret slice. The bearer, sessionId, chatSessionId,
      // cost etc. all stay in memory.
      partialize: (state): VoicePersistedState => ({
        lastProjectId: state.lastProjectId,
      }),
    },
  ),
);
